import type { Column } from "@legal-500/legal500-design-system";
import type { ChangeAuditEntry, ChangeAuditSource } from "@/api/changeAudit.service";
import {
  ActionPill,
  ActorCell,
  actionLabel,
  changedFieldNames,
  EntityIdCell,
  formatWhen,
} from "./auditCells";
import styles from "./AuditLog.module.css";
import { researchFieldLabel, researchTableLabel } from "./researchLabels";

export function createAuditLogColumns({
  source,
  onSelect,
}: {
  source: ChangeAuditSource;
  onSelect: (entry: ChangeAuditEntry) => void;
}): Column<ChangeAuditEntry>[] {
  const isResearch = source === "research-cms";
  const whenColumn: Column<ChangeAuditEntry> = {
    header: "When",
    accessor: "occurredAt",
    render: (_value, row) => <span className={styles.when}>{formatWhen(row.occurredAt)}</span>,
  };
  const whoColumn: Column<ChangeAuditEntry> = {
    header: "Who",
    accessor: "actor",
    render: (_value, row) => <ActorCell entry={row} />,
  };
  const actionColumn: Column<ChangeAuditEntry> = {
    header: "Action",
    accessor: "action",
    render: (_value, row) => <ActionPill action={actionLabel(source, row.action)} />,
  };
  const changedFieldsColumn: Column<ChangeAuditEntry> = {
    header: "Changed fields",
    accessor: "changes",
    render: (_value, row) => {
      const names = isResearch
        ? changedFieldNames(row).map(researchFieldLabel)
        : changedFieldNames(row);
      return (
        <span className={styles.changedFields} title={names.join(", ")}>
          {names.length ? names.join(", ") : "—"}
        </span>
      );
    },
  };
  const entityIdColumn: Column<ChangeAuditEntry> = {
    header: "Entity id",
    accessor: "entityId",
    render: (_value, row) => <EntityIdCell id={row.entityId} source={source} />,
  };
  const detailsColumn: Column<ChangeAuditEntry> = {
    header: "",
    accessor: "id",
    render: (_value, row) => (
      <button
        type="button"
        className={styles.linkButton}
        onClick={event => {
          event.stopPropagation();
          onSelect(row);
        }}
      >
        Details
      </button>
    ),
  };

  return isResearch
    ? [
        whenColumn,
        whoColumn,
        actionColumn,
        {
          header: "Table",
          accessor: "entityType",
          render: (_value, row) => (
            <span className={styles.cellText} title={row.entityType}>
              {researchTableLabel(row.entityType ?? "")}
            </span>
          ),
        },
        changedFieldsColumn,
        entityIdColumn,
        detailsColumn,
      ]
    : [
        whenColumn,
        whoColumn,
        actionColumn,
        {
          header: "Firm",
          accessor: "firmName",
          render: (_value, row) => (
            <span className={styles.cellText} title={row.firmName ?? undefined}>
              {row.firmName ?? "—"}
            </span>
          ),
        },
        {
          header: "Publication",
          accessor: "publicationName",
          render: (_value, row) => (
            <span className={styles.cellText} title={row.publicationName ?? undefined}>
              {row.publicationName ?? "—"}
            </span>
          ),
        },
        {
          header: "Lawyer",
          accessor: "lawyerName",
          render: (_value, row) => (
            <span className={styles.cellText} title={row.lawyerName ?? undefined}>
              {row.lawyerName ?? "—"}
            </span>
          ),
        },
        changedFieldsColumn,
        entityIdColumn,
        detailsColumn,
      ];
}
